import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { logger } from "@/lib/logger";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Save, CalendarClock, Info, Trash2 } from "lucide-react";
import { toast } from "sonner";

// Converte ISO UTC -> valor do <input type="datetime-local"> (fuso local do navegador)
function isoToLocalInput(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

// datetime-local -> ISO UTC
function localInputToIso(v) {
  if (!v) return null;
  const d = new Date(v);
  return d.toISOString();
}

const STATUS_META = {
  aberto: { label: "Aberto", cls: "bg-green-100 text-green-800" },
  encerrado: { label: "Encerrado", cls: "bg-red-100 text-red-800" },
  nao_iniciado: { label: "Ainda não iniciado", cls: "bg-yellow-100 text-yellow-800" },
  desativado: { label: "Desativado", cls: "bg-slate-200 text-slate-700" },
};

function statusBadge(status) {
  if (!status) return null;
  const key = status.aberto ? "aberto" : status.motivo || "desativado";
  const meta = STATUS_META[key] || STATUS_META.desativado;
  return <Badge className={meta.cls} data-testid="periodo-status-badge">{meta.label}</Badge>;
}

export default function PeriodoInscricoesPage() {
  const [inicio, setInicio] = useState("");
  const [fim, setFim] = useState("");
  const [ativo, setAtivo] = useState(true);
  const [status, setStatus] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/config/periodo-inscricoes");
      setInicio(isoToLocalInput(r.data.inicio));
      setFim(isoToLocalInput(r.data.fim));
      setAtivo(r.data.ativo !== false);
      setStatus(r.data.status);
    } catch (e) {
      logger.error("Falha ao carregar período", e);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const salvar = async () => {
    if (inicio && fim && new Date(inicio) >= new Date(fim)) {
      toast.error("A data de início deve ser anterior à data de fim");
      return;
    }
    setSaving(true);
    try {
      const r = await api.put("/config/periodo-inscricoes", {
        inicio: localInputToIso(inicio),
        fim: localInputToIso(fim),
        ativo,
      });
      setStatus(r.data.status);
      toast.success("Período salvo com sucesso!");
    } catch (e) {
      logger.error("Falha ao salvar período", e);
      toast.error(e.response?.data?.detail || "Erro ao salvar");
    } finally { setSaving(false); }
  };

  const limparDatas = () => { setInicio(""); setFim(""); };

  if (loading) return <div className="p-8 text-slate-500">Carregando...</div>;

  return (
    <div className="space-y-6" data-testid="periodo-inscricoes-page">
      <div>
        <h1 className="text-3xl font-black text-[#083CA6] flex items-center gap-3">
          <CalendarClock className="w-8 h-8" /> Período de Inscrições
        </h1>
        <p className="text-sm text-slate-600">
          Configure a data e a hora de <strong>início</strong> e <strong>fim</strong> das pré-inscrições públicas.
          Fora desse período, o formulário público é bloqueado. Cadastros feitos por funcionários no painel
          continuam permitidos a qualquer momento.
        </p>
      </div>

      <Alert className="border-[#083CA6]/30 bg-[#083CA6]/5">
        <Info className="w-4 h-4 text-[#083CA6]" />
        <AlertDescription className="text-sm">
          Deixe as datas em branco para inscrições sempre abertas (sem limite). Use o switch abaixo para
          suspender temporariamente sem apagar as datas.
        </AlertDescription>
      </Alert>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="text-base">Configuração</CardTitle>
            <CardDescription>Aplicado ao formulário público de inscrição.</CardDescription>
          </div>
          {statusBadge(status)}
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between p-3 bg-slate-50 rounded border">
            <div>
              <Label className="font-semibold">Inscrições Ativas</Label>
              <p className="text-xs text-slate-500">Desative para bloquear inscrições sem apagar as datas configuradas.</p>
            </div>
            <Switch checked={ativo} onCheckedChange={setAtivo} data-testid="switch-periodo-ativo" />
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="periodo-inicio">Início das Inscrições</Label>
              <Input
                id="periodo-inicio"
                type="datetime-local"
                value={inicio}
                onChange={(e) => setInicio(e.target.value)}
                data-testid="periodo-inicio"
              />
              <p className="text-xs text-slate-500 mt-1">Fuso local do seu navegador.</p>
            </div>
            <div>
              <Label htmlFor="periodo-fim">Fim das Inscrições</Label>
              <Input
                id="periodo-fim"
                type="datetime-local"
                value={fim}
                onChange={(e) => setFim(e.target.value)}
                data-testid="periodo-fim"
              />
              <p className="text-xs text-slate-500 mt-1">O formulário público será bloqueado após esta data.</p>
            </div>
          </div>

          <div className="flex flex-wrap gap-2 justify-end pt-2 border-t">
            <Button variant="outline" onClick={limparDatas} data-testid="btn-limpar-datas">
              <Trash2 className="w-4 h-4 mr-2" /> Limpar datas
            </Button>
            <Button onClick={salvar} disabled={saving}
                    className="bg-[#083CA6] hover:bg-[#062f85]" data-testid="btn-salvar-periodo">
              <Save className="w-4 h-4 mr-2" />{saving ? "Salvando..." : "Salvar configuração"}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
